import { logger } from '@fiverr-private/obs';
import { StoreWithSetter } from '../types';
import {
    MIXPANEL_STATISTICS_PREFIXES,
    MIXPANEL_STATISTICS_PROPS,
} from './constants';
import {
    keyWithPrefixBuilder,
    perEngineValues,
    perPoolValues,
    perStyleValues,
} from './helpers';
import {
    ActualListingStatistics,
    ExpectedListingStatistics,
    ListingStatistics,
} from './types';

export class StatisticsStore
    implements
        StoreWithSetter<ListingStatistics, Record<string, number> | null>
{
    public setData = (statistics: ListingStatistics) => {
        this._rawStatistics = statistics;

        this._processedStatistics = {
            ...this.getActualListingStatistics(statistics),
            ...this.getExpectedListingStatistics(statistics),
        };
    };

    public getData = () => {
        if (!this._processedStatistics) {
            const error = new Error(
                'Error in logo maker analytics. No statistics data was set'
            );
            logger.info(error);

            return null;
        }

        return this._processedStatistics;
    };

    public getRawData = () => this._rawStatistics;

    private _processedStatistics?: Record<string, number>;

    private _rawStatistics?: ListingStatistics;

    private getActualListingStatistics = ({
        actualAverageQuality,
        actualAverageVariationsQuality,
        actualAverageRpi,
        actualAverageRpiPerEngine,
        actualMinimalRpiPerEngine,
        actualMaximalRpiPerEngine,
        actualProjectsCount,
        actualProjectsCountPerPool,
        actualProjectsCountPerEngine,
        actualProjectsCountPerEngineWithExtra,
        actualStyleMatching,
        actualSingleStyleMatching,
    }: ActualListingStatistics) => ({
        [keyWithPrefixBuilder(
            MIXPANEL_STATISTICS_PREFIXES.ACTUAL,
            MIXPANEL_STATISTICS_PROPS.AVERAGE_QUALITY
        )]: actualAverageQuality,
        [keyWithPrefixBuilder(
            MIXPANEL_STATISTICS_PREFIXES.ACTUAL,
            MIXPANEL_STATISTICS_PROPS.AVERAGE_VARIATIONS_QUALITY
        )]: actualAverageVariationsQuality,
        [keyWithPrefixBuilder(
            MIXPANEL_STATISTICS_PREFIXES.ACTUAL,
            MIXPANEL_STATISTICS_PROPS.AVERAGE_RPI
        )]: actualAverageRpi,
        ...(actualAverageRpiPerEngine &&
            perEngineValues(
                actualAverageRpiPerEngine,
                MIXPANEL_STATISTICS_PREFIXES.ACTUAL,
                MIXPANEL_STATISTICS_PROPS.AVERAGE_RPI
            )),
        ...(actualMinimalRpiPerEngine &&
            perEngineValues(
                actualMinimalRpiPerEngine,
                MIXPANEL_STATISTICS_PREFIXES.ACTUAL,
                MIXPANEL_STATISTICS_PROPS.MINIMAL_RPI
            )),
        ...(actualMaximalRpiPerEngine &&
            perEngineValues(
                actualMaximalRpiPerEngine,
                MIXPANEL_STATISTICS_PREFIXES.ACTUAL,
                MIXPANEL_STATISTICS_PROPS.MAXIMAL_RPI
            )),
        [keyWithPrefixBuilder(
            MIXPANEL_STATISTICS_PREFIXES.ACTUAL,
            MIXPANEL_STATISTICS_PROPS.LOGOS_COUNT
        )]: actualProjectsCount,
        ...(actualProjectsCountPerPool &&
            perPoolValues(
                actualProjectsCountPerPool,
                MIXPANEL_STATISTICS_PREFIXES.ACTUAL,
                MIXPANEL_STATISTICS_PROPS.LOGOS_COUNT
            )),
        ...(actualProjectsCountPerEngine &&
            perEngineValues(
                actualProjectsCountPerEngine,
                MIXPANEL_STATISTICS_PREFIXES.ACTUAL,
                MIXPANEL_STATISTICS_PROPS.LOGOS_COUNT
            )),
        ...(actualProjectsCountPerEngineWithExtra &&
            perEngineValues(
                actualProjectsCountPerEngineWithExtra,
                MIXPANEL_STATISTICS_PREFIXES.ACTUAL,
                MIXPANEL_STATISTICS_PROPS.LOGOS_COUNT_WITH_EXTRA
            )),

        [keyWithPrefixBuilder(
            MIXPANEL_STATISTICS_PREFIXES.ACTUAL,
            MIXPANEL_STATISTICS_PROPS.STYLE_MATCHING
        )]: actualStyleMatching,
        ...(actualSingleStyleMatching &&
            perStyleValues(
                actualSingleStyleMatching,
                MIXPANEL_STATISTICS_PREFIXES.ACTUAL,
                MIXPANEL_STATISTICS_PROPS.SINGLE_STYLE_MATCHING
            )),
    });

    private getExpectedListingStatistics = ({
        expectedProjectsCount,
        expectedEnginesAmountDistribution,
        expectedEnginesPerPageDistribution,
        expectedEnginesPerListingDistribution,
        expectedPoolsPerListingDistribution,
        expectedPoolsPerPageDistribution,
    }: ExpectedListingStatistics) => ({
        [keyWithPrefixBuilder(
            MIXPANEL_STATISTICS_PREFIXES.EXPECTED,
            MIXPANEL_STATISTICS_PROPS.LOGOS_COUNT
        )]: expectedProjectsCount,
        ...(expectedEnginesAmountDistribution &&
            perEngineValues(
                expectedEnginesAmountDistribution,
                MIXPANEL_STATISTICS_PREFIXES.EXPECTED,
                MIXPANEL_STATISTICS_PROPS.LOGOS_PERCENTAGE
            )),
        ...(expectedEnginesPerListingDistribution &&
            perEngineValues(
                expectedEnginesPerListingDistribution,
                MIXPANEL_STATISTICS_PREFIXES.EXPECTED,
                MIXPANEL_STATISTICS_PROPS.LOGOS_COUNT
            )),
        ...(expectedPoolsPerListingDistribution &&
            perPoolValues(
                expectedPoolsPerListingDistribution,
                MIXPANEL_STATISTICS_PREFIXES.EXPECTED,
                MIXPANEL_STATISTICS_PROPS.LOGOS_COUNT
            )),
        ...(expectedEnginesPerPageDistribution &&
            perEngineValues(
                expectedEnginesPerPageDistribution,
                MIXPANEL_STATISTICS_PREFIXES.EXPECTED,
                MIXPANEL_STATISTICS_PROPS.LOGOS_COUNT_PER_PAGE
            )),
        ...(expectedPoolsPerPageDistribution &&
            perPoolValues(
                expectedPoolsPerPageDistribution,
                MIXPANEL_STATISTICS_PREFIXES.EXPECTED,
                MIXPANEL_STATISTICS_PROPS.LOGOS_COUNT_PER_PAGE
            )),
    });
}
